import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getFilteredGamesStart,
  getGameSubCategoryStart,
  getCategoryProviderListStart
} from 'store/redux-slices/casinoMenu'
import { getItem } from 'utils/storageUtils'
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom'
import { showFooter } from 'store/redux-slices/loader'
import { Routes } from 'utils/routes'
import { deviceType } from 'components/DashboardSections/Constant'
import { GAME_FILTER_SUPPORT_TYPE, GAME_CATEGORY_EXTRA_TAB_SET } from '../../../constants/uiConfig'

const useFilterGames = ({ selectedFilter, setSelectedFilter }) => {
  const { state } = useLocation()
  const { casinoCategory, casinoSubCategory } = useParams()
  const navTabSubCategory = useRef()
  const matchesCasinoRoute = useMatch(Routes.casinoSubCategory)
  const matchesPromotionRoute = useMatch(Routes.promotionpageId)

  // Determines if the current route is a casino route by checking route matches
  // Returns false for promotion routes, true for casino routes
  const isCasinoRoute = useMemo(() => {
    if (matchesPromotionRoute) {
      return false
    }
    return !!matchesCasinoRoute
  }, [matchesCasinoRoute, matchesPromotionRoute])

  const {
    gameCategory,
    gameSubCategory,
    providers = [],
    studiosByOrder = [],
    filteredGames,
    filteredLoading,
    fetchMoreLoading,
    rowsFetchedCount,
    subCatLoading
  } = useSelector((state) => state.casinoMenu)

  // Build studioId -> name map from studiosByOrder (derived from providers API)
  const studioNameById = useMemo(() => {
    const map = {}
    providers.forEach(provider => {
      if (provider && provider.TenantGameStudios) {
        provider.TenantGameStudios.forEach(({ GameStudio, id }) => {
          if (GameStudio && id != null && GameStudio?.name) {
            map[id] = GameStudio?.name
          }
        })
      }
    })
    return map
  }, [providers])

  // Get a selected category object based on route:
  // - For casino routes: find category matching URL parameter
  // - For other routes: return first category
  const selectedCategoryObj = useMemo(() => {
    if (isCasinoRoute) {
      return gameCategory?.casinoCategories?.rows?.find(item => item?.slug === casinoCategory)
    }
    return gameCategory?.casinoCategories?.rows?.[0]
  }, [gameCategory, casinoCategory, isCasinoRoute])

  const loggedIn = JSON.parse(getItem('loggedIn'))
  const [page, setPage] = useState(1)
  const limit = 20
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { selectedLanguageCode, languageData } = useSelector((state) => state.language)
  const { userDetails } = useSelector((state) => state.user)
  const [selectedMenu, setSelectedMenu] = useState(null)
  const [loadAllData, setLoadAllData] = useState(false)
  const uiConfig = useSelector(state => state?.uiConfig?.general)
  const initialRender = useRef(true)

  // For Sub-Category change from url
  // Handle subcategory changes from URL:
  // - Scroll to subcategory tab position
  // - Navigate to 404 if category not found
  // - Change menu tab or navigate to 'all' based on subcategory existence
  useEffect(() => {
    if (!isCasinoRoute) return

    const categoryRows = gameCategory?.casinoCategories?.rows
    const subCategoryRows = gameSubCategory?.rows

    // Wait for async data before resolving the URL slug; otherwise the initial
    // mount on a direct URL hit (e.g. /en/casino/popular) would resolve
    // subCategoryId to undefined and never recover, since this effect doesn't
    // re-run when the data eventually lands.
    if (!categoryRows?.length || !subCategoryRows?.length) return

    if (!uiConfig?.disableAutoScrollToSubCategory && navTabSubCategory?.current?.parentElement?.offsetTop) {
      window?.scrollTo({
        top: ((navTabSubCategory.current.parentElement.offsetTop / 100) - 2.1) * 100
      })
    }

    if (!categoryRows?.some(cat => cat?.slug === casinoCategory)) {
      navigate(`/${selectedLanguageCode?.toLowerCase()}/404`)
      return
    }

    const subCategoryId = casinoSubCategory !== 'all' ? subCategoryRows?.find(({ slug }) => slug === casinoSubCategory)?.id : null

    // Extra tabs (favorites / recent games) only render for logged-in users and
    // must be configured for the tenant (mirrors GameCategories). When the slug
    // isn't an available extra tab, treat it like any invalid sub-category below.
    const configuredExtraTabs = loggedIn ? (uiConfig?.gameCategoryExtraTabs || []) : []
    const isAvailableExtraTab = GAME_CATEGORY_EXTRA_TAB_SET.has(casinoSubCategory) &&
      configuredExtraTabs.some(tab => tab?.key === casinoSubCategory)

    // AMGP-3667: Skip stale URL-sync updates when a newer tab click has already
    // changed the URL. This prevents the pending effect from overwriting the
    // newer selection and causing an effect/navigation loop.

    const urlMovedOn = () =>
      decodeURIComponent(window.location.pathname.split('/').filter(Boolean).pop() || '') !== casinoSubCategory

    if (subCategoryId) {
      // No-op when the state already reflects this subCategoryId — avoids
      // producing a new selectedFilter object (and re-render) on every URL sync,
      // which otherwise feeds back into the fetch/navigate effect below and can
      // cycle indefinitely under fast successive category switches.
      setSelectedFilter(selectedFilter => (
        urlMovedOn() || (selectedFilter?.subCategoryId === subCategoryId && selectedFilter?.pageNo === 1 && selectedFilter?.category === null)
          ? selectedFilter
          : { ...selectedFilter, pageNo: 1, subCategoryId, category: null }
      ))
    } else if (isAvailableExtraTab) {
      // Mirror the tab-click handler in GameCategories by setting subCategoryId to
      // the slug. This keeps the extra tab (not "Lobby") as the active one and lets
      // the selected-tab effect fire.
      setSelectedFilter(selectedFilter => (
        urlMovedOn() || (selectedFilter?.subCategoryId === casinoSubCategory && selectedFilter?.pageNo === 1)
          ? selectedFilter
          : { ...selectedFilter, pageNo: 1, subCategoryId: casinoSubCategory, category: [casinoSubCategory] }
      ))
    } else if (casinoSubCategory !== 'all') {
      if (!urlMovedOn()) navigate(`/${selectedLanguageCode?.toLowerCase()}/${casinoCategory}/all`)
    } else {
      // URL changed to 'all' (e.g. via the header Casino link) but a sub-category is
      // still selected — clear it, same as clicking the Lobby tab.
      // If nothing is selected, keep the same object to skip a re-render.
      setSelectedFilter(selectedFilter => (
        !urlMovedOn() && (selectedFilter?.subCategoryId || selectedFilter?.category?.length)
          ? { ...selectedFilter, pageNo: 1, category: [], provider: [], studio: [], rating: [], subCategoryId: null }
          : selectedFilter
      ))
    }
    setLoadAllData(false)
  }, [casinoSubCategory, state?.reload, gameCategory, gameSubCategory])

  useEffect(() => {
    const categoryId = selectedCategoryObj?.id || gameCategory?.casinoCategories?.rows?.[0]?.id

    if (categoryId) {
      dispatch(getGameSubCategoryStart({
        categoryId,
        deviceType
      }))
    }
  }, [selectedCategoryObj, gameCategory])

  useEffect(() => {
    dispatch(showFooter(true))
  }, [])

  // TODO:- fix this category provider
  // useEffect(() => {
  //   if (selectedMenu && typeof selectedMenu === 'number') {
  //     dispatch(getCategoryProviderListStart({ category: selectedMenu }))
  //   }
  // }, [])

  /**
   * @typedef {Object} SelectedFilter
   * @property {number} limit - The limit for the number of results per page.
   * @property {Array<string>} category - The category to filter games by.
   * @property {string} search - The search query for filtering games.
   * @property {Array<number>} rating - The rating filter for games.
   * @property {Array<string>} provider - The providers to filter games by.
   * @property {number} pageNo - The current page number in the filter.
   */

  /**
   * Generates filter parameters for fetching games based on the selected filter, page number, and whether more data is being fetched.
   *
   * @param {SelectedFilter} selectedFilter - The selected filter options.
   * @param {number} myPageNo - The page number to fetch.
   * @param {boolean} fetchMore - Indicates whether more data is being fetched (pagination).
   * @returns {Object} The filter parameters formatted for the API request.
   */
  const getFilterParams = (selectedFilter, myPageNo, fetchMore) => {
    const selectedProviderNames = Array.isArray(selectedFilter?.provider) ? selectedFilter.provider : []
    const selectedStudioIds = Array.isArray(selectedFilter?.studio) ? selectedFilter.studio : []

    // Provider-only support: when enabled, send providers and ignore studios
    if (uiConfig?.gameFilterSupportType === GAME_FILTER_SUPPORT_TYPE.PROVIDER) {
      return ({
        limit: selectedFilter?.limit,
        pageNo: myPageNo,
        category: JSON.stringify(selectedFilter?.category),
        subCategoryId: selectedFilter?.subCategoryId,
        search: selectedFilter?.search,
        rating: JSON.stringify(selectedFilter?.rating),
        provider: selectedProviderNames,
        studio: [],
        fetchMore,
        gameInputSearch: false,
        deviceType
      })
    }

    // Build studioId -> providerName map and providerName -> studio names set
    const providerToStudioNames = {}
    providers.forEach(provider => {
      const providerName = provider?.name
      const studios = Array.isArray(provider?.TenantGameStudios) ? provider.TenantGameStudios : []

      // Only build names map if this provider is explicitly selected
      if (selectedProviderNames.includes(providerName)) {
        studios.forEach(studio => {
          const studioName = studio?.GameStudio?.name
          if (!studioName) return
          if (!providerToStudioNames[providerName]) providerToStudioNames[providerName] = new Set()
          providerToStudioNames[providerName].add(studioName)
        })
      }
    })

    // Studio names from explicit studio selections (via id -> name)
    const studioNamesFromIds = selectedStudioIds
      .map(id => studioNameById[id])
      .filter(Boolean)

    // Studio names from explicitly selected providers
    const studioNamesFromProviders = Object.values(providerToStudioNames)
      .reduce((acc, set) => acc.concat(Array.from(set)), [])

    // Final studio names: union of both sources, URL-encoded
    const studioNames = Array.from(new Set([
      ...studioNamesFromIds,
      ...studioNamesFromProviders
    ]))

    return ({
      limit: selectedFilter?.limit,
      pageNo: myPageNo,
      category: JSON.stringify(selectedFilter?.category),
      subCategoryId: selectedFilter?.subCategoryId,
      search: selectedFilter?.search,
      rating: JSON.stringify(selectedFilter?.rating),
      studio: studioNames,
      fetchMore,
      gameInputSearch: false,
      deviceType
    })
  }

  const fetchGames = ({ myPageNo = 1, fetchMore = false }) => {
    const params = getFilterParams(selectedFilter, myPageNo, fetchMore)
    dispatch(getFilteredGamesStart(params))

    // Scroll to the top when filters trigger a fresh fetch
    if (!fetchMore) {
      const topPos = navTabSubCategory?.current?.parentElement?.offsetTop ?? navTabSubCategory?.current?.offsetParent?.offsetTop
      if (typeof topPos === 'number') {
        window?.scrollTo({
          top: ((topPos / 100) - 2.5) * 100
        })
      } else {
        window?.scrollTo({ top: 0 })
      }
    }
  }

  useEffect(() => {
    if (initialRender.current) {
      //TODO:- Need to refactor this whole filter use effect along with another.
        initialRender.current = false
    } else {
      if (isCasinoRoute) {
        const activeCategory = selectedFilter?.category?.[0]
        const isExtraTab = GAME_CATEGORY_EXTRA_TAB_SET.has(activeCategory)

        if (isExtraTab) {
          setSelectedMenu(activeCategory)
          setLoadAllData(false)
          navigate(`/${getItem('language')?.toLowerCase()}/${casinoCategory}/${activeCategory}`, { replace: true })
          return
        }

        fetchGames({ myPageNo: 1, fetchMore: false })

        if (selectedFilter?.subCategoryId) {
          const targetSlug = gameSubCategory?.rows
            ?.find(({ id }) => id === selectedFilter.subCategoryId)
            ?.slug

          // Only navigate when we actually resolved a slug and it differs from the
          // current URL. Navigating to an unresolved slug (e.g. gameSubCategory
          // hasn't loaded yet for the newly selected category) sends the URL to
          // ".../undefined", which the URL-sync effect above then has to correct —
          // and re-navigating to a URL we're already at is a pure no-op that only
          // adds churn. Either way this was a source of feedback loops when
          // switching categories in quick succession.
          if (targetSlug && targetSlug !== casinoSubCategory) {
            navigate(`/${getItem('language')?.toLowerCase()}/${casinoCategory}/${targetSlug}`, { replace: true })
          }

          // setSelectedMenu(categoryRoute || 0)
          setLoadAllData(false)

          // Only reset pageNo if it's not already 1 (prevents infinite loop)
          if (selectedFilter?.pageNo !== 1) {
            setSelectedFilter(selectedFilter => ({ ...selectedFilter, pageNo: 1 }))
          }
        } else if (casinoSubCategory !== 'all' && gameSubCategory?.rows?.length) {
          // Lobby / "All" selected — reflect 'all' in the URL when it isn't already.
          // Guarded on sub-categories being loaded: on a direct deep-link
          // (/<category>/<sub-slug>) this effect can fire before the sub-categories
          // arrive, while subCategoryId is still unresolved — redirecting then would
          // clobber the deep-linked slug before the resolver effect can apply it.
          navigate(`/${getItem('language')?.toLowerCase()}/${casinoCategory}/all`, { replace: true })
          setLoadAllData(false)
        }
      }
    }
  }, [
    selectedFilter?.search,
    selectedFilter?.subCategoryId,
    selectedFilter?.rating,
    selectedFilter?.provider,
    selectedFilter?.studio,
    initialRender.current
  ])

  const fetchMoreGames = () => {
    fetchGames({ myPageNo: selectedFilter?.pageNo + 1, fetchMore: true })
    setSelectedFilter(selectedFilter => ({ ...selectedFilter, pageNo: selectedFilter?.pageNo + 1 }))
  }

  return {
    page,
    limit,
    setPage,
    navigate,
    loggedIn,
    dispatch,
    providers,
    studios: studiosByOrder,
    loadAllData,
    userDetails,
    gameCategory,
    selectedMenu,
    languageData,
    // changeMenuTab,
    filteredGames,
    fetchMoreGames,
    casinoCategory,
    setLoadAllData,
    gameSubCategory,
    rowsFetchedCount,
    navTabSubCategory,
    selectedLanguageCode,
    selectedCategoryObj,
    subCatLoading,
    fetchMoreLoading,
    loading: filteredLoading
  }
}

export default useFilterGames
